import { useState, useEffect, useCallback, useRef } from "react";
import { MainLayout } from "../components/layout/MainLayout";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { toast } from "sonner";
import {
  Store, Plus, Building2, Link, Copy, TrendingUp,
  Loader2, Trash2, Gift
} from "lucide-react";

// === STORES TAB ===
function StoresTab({ authAxios }) {
  const authAxiosRef = useRef(authAxios);
  authAxiosRef.current = authAxios;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState("");

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const fetchStores = useCallback(async () => {
    setLoading(true);
    try { const res = await authAxiosRef.current.get("/franchise/stores"); setData(res.data); }
    catch { toast.error("Error al cargar tiendas"); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchStores(); }, [fetchStores]);

  const createStore = async () => {
    if (!newName.trim()) return;
    try {
      await authAxios.post("/franchise/stores", { name: newName });
      toast.success("Tienda creada");
      setNewName("");
      setShowCreate(false);
      fetchStores();
    } catch { toast.error("Error al crear tienda"); }
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-emerald-600" /></div>;

  const stores = data?.stores || [];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="font-bold text-sm text-gray-700">Mis Tiendas ({stores.length})</h3>
        <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white gap-1" onClick={() => setShowCreate(!showCreate)} data-testid="create-store-btn">
          <Plus className="w-3 h-3" /> Nueva Tienda
        </Button>
      </div>

      {showCreate && (
        <div className="bg-gray-50 rounded-xl p-4 border flex gap-2">
          <Input value={newName} onChange={e => setNewName(e.target.value)} placeholder="Nombre de la tienda" className="flex-1" data-testid="new-store-name" />
          <Button onClick={createStore} className="bg-emerald-600 hover:bg-emerald-700 text-white">Crear</Button>
          <Button variant="outline" onClick={() => setShowCreate(false)}>Cancelar</Button>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="stores-grid">
        {stores.map(store => (
          <div key={store.id} className="bg-white rounded-xl border shadow-sm p-4 hover:border-emerald-200 transition" data-testid={`store-card-${store.id}`}>
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                {store.logo ? <img src={store.logo} alt="" className="w-10 h-10 rounded-lg object-cover" /> : <Store className="w-5 h-5 text-emerald-600" />}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-bold text-sm text-gray-800 truncate">{store.name}</p>
                <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-bold ${store.subscription_status === "active" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
                  {store.subscription_plan === "trial" ? "PRUEBA" : store.subscription_plan?.toUpperCase()}
                </span>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2 text-center">
              <div className="bg-gray-50 rounded-lg p-2">
                <p className="text-lg font-bold text-gray-800">{store.total_orders}</p>
                <p className="text-[10px] text-gray-400">Pedidos</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-2">
                <p className="text-lg font-bold text-emerald-600">{store.total_revenue?.toFixed(0)}€</p>
                <p className="text-[10px] text-gray-400">Revenue</p>
              </div>
            </div>
          </div>
        ))}
        {stores.length === 0 && (
          <div className="col-span-full text-center py-8 text-gray-400">
            <Store className="w-10 h-10 mx-auto mb-2 text-gray-300" />
            <p className="text-sm">No tienes tiendas creadas</p>
          </div>
        )}
      </div>
    </div>
  );
}

// === FRANCHISES TAB ===
function FranchisesTab({ authAxios }) {
  const authAxiosRef = useRef(authAxios);
  authAxiosRef.current = authAxios;
  const [franchises, setFranchises] = useState([]);
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ name: "", subtitle: "", url: "" });
  const [selectedStores, setSelectedStores] = useState([]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [fRes, sRes] = await Promise.all([
        authAxiosRef.current.get("/franchise/franchises"),
        authAxiosRef.current.get("/franchise/stores"),
      ]);
      setFranchises(fRes.data.franchises || []);
      setStores(sRes.data.stores || []);
    } catch { toast.error("Error al cargar franquicias"); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const createFranchise = async () => {
    if (!form.name.trim()) return;
    try {
      await authAxios.post("/franchise/franchises", { ...form, store_ids: selectedStores });
      toast.success("Franquicia creada");
      setShowCreate(false);
      setForm({ name: "", subtitle: "", url: "" });
      setSelectedStores([]);
      fetchData();
    } catch { toast.error("Error al crear franquicia"); }
  };

  const deleteFranchise = async (id) => {
    try {
      await authAxios.delete(`/franchise/franchises/${id}`);
      toast.success("Franquicia eliminada");
      fetchData();
    } catch { toast.error("Error al eliminar"); }
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-emerald-600" /></div>;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="font-bold text-sm text-gray-700">Mis Franquicias ({franchises.length})</h3>
        <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white gap-1" onClick={() => setShowCreate(!showCreate)} data-testid="create-franchise-btn">
          <Plus className="w-3 h-3" /> Nueva Franquicia
        </Button>
      </div>

      {showCreate && (
        <div className="bg-gray-50 rounded-xl border p-4 space-y-3">
          <Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Nombre (ej: Kaiso Group)" data-testid="franchise-name" />
          <Input value={form.subtitle} onChange={e => setForm(f => ({ ...f, subtitle: e.target.value }))} placeholder="Subtitulo (ej: Escolha a unidade mais proxima)" />
          <Input value={form.url} onChange={e => setForm(f => ({ ...f, url: e.target.value }))} placeholder="URL personalizada (ej: kaiso.com/franquia)" />
          <div>
            <p className="text-xs font-semibold text-gray-500 mb-2">Seleccionar tiendas:</p>
            <div className="grid grid-cols-2 gap-2">
              {stores.map(s => (
                <label key={s.id} className={`flex items-center gap-2 p-2 rounded-lg border cursor-pointer text-sm ${selectedStores.includes(s.id) ? "bg-emerald-50 border-emerald-300" : "hover:bg-gray-50"}`}>
                  <input type="checkbox" checked={selectedStores.includes(s.id)} onChange={() => setSelectedStores(prev => prev.includes(s.id) ? prev.filter(id => id !== s.id) : [...prev, s.id])} />
                  {s.name}
                </label>
              ))}
            </div>
          </div>
          <div className="flex gap-2">
            <Button onClick={createFranchise} className="bg-emerald-600 hover:bg-emerald-700 text-white">Crear Franquicia</Button>
            <Button variant="outline" onClick={() => setShowCreate(false)}>Cancelar</Button>
          </div>
        </div>
      )}

      <div className="space-y-3" data-testid="franchises-list">
        {franchises.map(f => (
          <div key={f.id} className="bg-white rounded-xl border shadow-sm p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-violet-100 flex items-center justify-center">
                  {f.logo ? <img src={f.logo} alt="" className="w-12 h-12 rounded-xl object-cover" /> : <Building2 className="w-6 h-6 text-violet-600" />}
                </div>
                <div>
                  <p className="font-bold text-gray-800">{f.name}</p>
                  <p className="text-xs text-gray-400">{f.subtitle || "Escolha a unidade mais proxima"}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className="bg-violet-100 text-violet-700 text-xs font-bold px-2 py-1 rounded-lg">{f.store_count || 0} tiendas</span>
                <Button variant="ghost" size="sm" onClick={() => deleteFranchise(f.id)} data-testid={`delete-franchise-${f.id}`}>
                  <Trash2 className="w-4 h-4 text-gray-400" />
                </Button>
              </div>
            </div>
            {f.url && <p className="text-xs text-blue-500 flex items-center gap-1"><Link className="w-3 h-3" /> {f.url}</p>}
          </div>
        ))}
        {franchises.length === 0 && (
          <div className="text-center py-8 text-gray-400">
            <Building2 className="w-10 h-10 mx-auto mb-2 text-gray-300" />
            <p className="text-sm">No tienes franquicias. Crea una para agrupar tus tiendas.</p>
          </div>
        )}
      </div>
    </div>
  );
}

// === CONSOLIDATED REPORTS TAB ===
function ReportsTab({ authAxios }) {
  const authAxiosRef = useRef(authAxios);
  authAxiosRef.current = authAxios;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const fetchData = useCallback(async () => {
    setLoading(true);
    try { const res = await authAxiosRef.current.get("/franchise/reports"); setData(res.data); }
    catch { toast.error("Error al cargar reportes"); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-emerald-600" /></div>;
  if (!data) return null;

  const totals = data.totals || {};

  return (
    <div className="space-y-4">
      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="bg-white rounded-xl p-4 border shadow-sm">
          <p className="text-xs text-gray-400 uppercase">Tiendas</p>
          <p className="text-2xl font-bold text-gray-800 mt-1" data-testid="report-stores">{totals.stores_count}</p>
        </div>
        <div className="bg-white rounded-xl p-4 border shadow-sm">
          <p className="text-xs text-gray-400 uppercase">Total Pedidos</p>
          <p className="text-2xl font-bold text-blue-600 mt-1">{totals.orders}</p>
        </div>
        <div className="bg-white rounded-xl p-4 border shadow-sm">
          <p className="text-xs text-gray-400 uppercase">Revenue Total</p>
          <p className="text-2xl font-bold text-emerald-600 mt-1" data-testid="report-revenue">{totals.revenue?.toFixed(0)}€</p>
        </div>
        <div className="bg-white rounded-xl p-4 border shadow-sm">
          <p className="text-xs text-gray-400 uppercase">Ticket Medio</p>
          <p className="text-2xl font-bold text-gray-800 mt-1">{totals.avg_ticket?.toFixed(2)}€</p>
        </div>
      </div>

      {/* Per-Store Breakdown */}
      <div className="bg-white rounded-xl border shadow-sm overflow-hidden">
        <div className="p-4 border-b">
          <h3 className="font-bold text-sm text-gray-700">Performance por Tienda</h3>
        </div>
        <div className="divide-y" data-testid="store-performance">
          {(data.stores || []).map(store => {
            const revPct = totals.revenue > 0 ? (store.total_revenue / totals.revenue * 100) : 0;
            return (
              <div key={store.id} className="flex items-center px-4 py-3 hover:bg-gray-50">
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <div className="w-8 h-8 rounded-lg bg-emerald-100 flex items-center justify-center">
                    <Store className="w-4 h-4 text-emerald-600" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-gray-800 truncate">{store.name}</p>
                    <p className="text-xs text-gray-400">{store.total_orders} pedidos</p>
                  </div>
                </div>
                <div className="text-right mx-4">
                  <p className="text-sm font-bold text-gray-800">{store.total_revenue?.toFixed(0)}€</p>
                  <p className="text-xs text-gray-400">{revPct.toFixed(0)}% del total</p>
                </div>
                <div className="w-24 h-2 bg-gray-100 rounded-full overflow-hidden">
                  <div className="h-full bg-emerald-500 rounded-full" style={{ width: `${Math.min(100, revPct)}%` }} />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// === AMBASSADOR TAB ===
function AmbassadorTab({ authAxios }) {
  const authAxiosRef = useRef(authAxios);
  authAxiosRef.current = authAxios;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const fetchData = useCallback(async () => {
    setLoading(true);
    try { const res = await authAxiosRef.current.get("/franchise/ambassador"); setData(res.data); }
    catch { toast.error("Error al cargar programa"); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const copyLink = () => {
    if (data?.link) {
      navigator.clipboard.writeText(data.link);
      toast.success("Link copiado!");
    }
  };

  const copyCode = () => {
    if (data?.code) {
      navigator.clipboard.writeText(data.code);
      toast.success("Codigo copiado!");
    }
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-emerald-600" /></div>;
  if (!data) return null;

  return (
    <div className="space-y-4">
      {/* Ambassador Card */}
      <div className="bg-gradient-to-br from-violet-600 to-indigo-700 rounded-2xl p-6 text-white">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
            <Gift className="w-6 h-6 text-white" />
          </div>
          <div>
            <h3 className="text-xl font-bold">Programa de Embajadores</h3>
            <p className="text-violet-200 text-sm">Gana {data.commission_rate}% de comision por cada referencia</p>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3 mb-4">
          <div className="bg-white/10 rounded-xl p-3">
            <p className="text-violet-200 text-xs uppercase">Tu Codigo</p>
            <div className="flex items-center gap-2 mt-1">
              <p className="text-2xl font-black" data-testid="ambassador-code">{data.code}</p>
              <button onClick={copyCode} className="p-1 hover:bg-white/10 rounded"><Copy className="w-4 h-4" /></button>
            </div>
          </div>
          <div className="bg-white/10 rounded-xl p-3">
            <p className="text-violet-200 text-xs uppercase">Comision</p>
            <p className="text-2xl font-black mt-1">{data.commission_rate}%</p>
          </div>
        </div>

        <div className="bg-white/10 rounded-xl p-3">
          <p className="text-violet-200 text-xs uppercase mb-1">Tu Link de Referencia</p>
          <div className="flex items-center gap-2">
            <p className="text-sm truncate flex-1 font-mono" data-testid="ambassador-link">{data.link}</p>
            <button onClick={copyLink} className="p-2 hover:bg-white/10 rounded-lg transition" data-testid="copy-link-btn">
              <Copy className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="bg-white rounded-xl p-4 border shadow-sm">
          <p className="text-xs text-gray-400 uppercase">Referidos</p>
          <p className="text-2xl font-bold text-gray-800 mt-1" data-testid="total-referrals">{data.total_referrals}</p>
        </div>
        <div className="bg-white rounded-xl p-4 border shadow-sm">
          <p className="text-xs text-gray-400 uppercase">Comision Total</p>
          <p className="text-2xl font-bold text-emerald-600 mt-1">{data.total_commission?.toFixed(2)}€</p>
        </div>
        <div className="bg-white rounded-xl p-4 border shadow-sm">
          <p className="text-xs text-gray-400 uppercase">Pagado</p>
          <p className="text-2xl font-bold text-blue-600 mt-1">{data.paid_commission?.toFixed(2)}€</p>
        </div>
        <div className="bg-white rounded-xl p-4 border shadow-sm">
          <p className="text-xs text-gray-400 uppercase">Pendiente</p>
          <p className="text-2xl font-bold text-amber-600 mt-1">{data.pending_commission?.toFixed(2)}€</p>
        </div>
      </div>

      {/* Referral History */}
      {data.referrals && data.referrals.length > 0 && (
        <div className="bg-white rounded-xl border shadow-sm overflow-hidden" data-testid="referrals-list">
          <div className="p-4 border-b">
            <h3 className="font-bold text-sm text-gray-700">Historial de Referidos</h3>
          </div>
          <div className="divide-y">
            {data.referrals.map((r, i) => (
              <div key={i} className="flex items-center px-4 py-3 text-sm">
                <div className="flex-1">
                  <p className="font-medium text-gray-800">{r.referred_user_id?.slice(0, 8)}...</p>
                  <p className="text-xs text-gray-400">{new Date(r.created_at).toLocaleDateString("es-ES")}</p>
                </div>
                <div className="text-right">
                  <p className="font-bold text-gray-800">{r.commission?.toFixed(2)}€</p>
                  <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-bold ${r.paid ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
                    {r.paid ? "PAGADO" : "PENDIENTE"}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* How to share */}
      <div className="bg-gray-50 rounded-xl p-4 text-sm text-gray-500">
        <p className="font-bold text-gray-700 mb-2">Como funciona:</p>
        <ul className="space-y-1 text-xs list-disc ml-4">
          <li>Comparte tu link o codigo con otros restaurantes</li>
          <li>Cuando se registren y paguen, ganas {data.commission_rate}% de su suscripcion</li>
          <li>Las comisiones se acumulan mensualmente</li>
          <li>Pago mensual via transferencia bancaria</li>
        </ul>
      </div>
    </div>
  );
}

// === MAIN PAGE ===
export default function FranchisePage() {
  const { authAxios } = useAuth();

  return (
    <MainLayout>
      <div className="p-6 max-w-6xl mx-auto">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-800">Franquicias & Multi-Tienda</h1>
          <p className="text-sm text-gray-500 mt-1">Gestiona todas tus tiendas, franquicias y programa de embajadores</p>
        </div>

        <Tabs defaultValue="stores">
          <TabsList className="mb-4 flex-wrap gap-1">
            <TabsTrigger value="stores" className="gap-1"><Store className="w-3 h-3" /> Tiendas</TabsTrigger>
            <TabsTrigger value="franchises" className="gap-1"><Building2 className="w-3 h-3" /> Franquicias</TabsTrigger>
            <TabsTrigger value="reports" className="gap-1"><TrendingUp className="w-3 h-3" /> Reportes</TabsTrigger>
            <TabsTrigger value="ambassador" className="gap-1"><Gift className="w-3 h-3" /> Embajadores</TabsTrigger>
          </TabsList>

          <TabsContent value="stores"><StoresTab authAxios={authAxios} /></TabsContent>
          <TabsContent value="franchises"><FranchisesTab authAxios={authAxios} /></TabsContent>
          <TabsContent value="reports"><ReportsTab authAxios={authAxios} /></TabsContent>
          <TabsContent value="ambassador"><AmbassadorTab authAxios={authAxios} /></TabsContent>
        </Tabs>
      </div>
    </MainLayout>
  );
}
